const finitePrice = (value) => {
  if (value == null || value === '') return null;
  const amount = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
};

/**
 * Parse a response-style `price` field ("0.25x", 0.5, "free").
 * @param {number|string|null|undefined} input
 * @returns {number}
 */
const parseStylePriceMultiplier = (input) => {
  if (input == null || input === '') return NaN;
  if (typeof input === 'number') return Number.isFinite(input) ? input : NaN;

  const str = String(input).trim().toLowerCase();
  if (str === 'free') return 1;

  const parsed = parseFloat(str.replace(/x$/i, ''));
  return Number.isFinite(parsed) ? parsed : NaN;
};

/**
 * Cache-aware USD per 1M tokens. Input is the cache-read rate when published.
 * @param {{ prompt?: number|string, completion?: number|string, input_cache_read?: number|string }|null|undefined} pricing
 * @returns {number|null}
 */
export const weightedUsdPerMillion = (pricing) => {
  if (!pricing || typeof pricing !== 'object') return null;
  const prompt = finitePrice(pricing.prompt);
  const completion = finitePrice(pricing.completion);
  if (prompt == null || completion == null) return null;
  const cacheRead = finitePrice(pricing.input_cache_read);
  const input = cacheRead == null ? prompt : cacheRead;
  return (input + completion) / 2;
};

/**
 * OpenRouter quotes USD per token. Scale to the per-million units stored on styles.
 * @param {object|null|undefined} pricing
 * @returns {{ prompt: number, completion: number, input_cache_read?: number }|null}
 */
export const openRouterPricingPerMillion = (pricing) => {
  if (!pricing || typeof pricing !== 'object') return null;
  const scale = (value) => {
    const amount = finitePrice(value);
    return amount == null ? null : amount * 1000000;
  };
  const prompt = scale(pricing.prompt);
  const completion = scale(pricing.completion);
  if (prompt == null || completion == null) return null;
  const perMillion = { prompt, completion };
  const cacheRead = scale(pricing.input_cache_read);
  if (cacheRead != null) perMillion.input_cache_read = cacheRead;
  return perMillion;
};

/**
 * Weighted USD/1M of the active style whose credit multiplier is exactly 1.
 * @param {Array<object>|null|undefined} styles
 * @returns {number|null}
 */
export const findPriceAnchor = (styles) => {
  if (!Array.isArray(styles)) return null;
  for (const style of styles) {
    if (!style?.name) continue;
    if (parseStylePriceMultiplier(style.price) !== 1) continue;
    const weighted = weightedUsdPerMillion(style.pricing);
    if (weighted == null || weighted <= 0) continue;
    return weighted;
  }
  return null;
};

/**
 * Credit multiplier vs the 1x anchor, rounded to the nearest 0.1 and at least 0.1.
 * @param {object|null|undefined} pricing USD per 1M tokens
 * @param {number|null|undefined} anchorWeighted
 * @returns {number|null}
 */
export const creditMultiplierFromPricing = (pricing, anchorWeighted) => {
  const weighted = weightedUsdPerMillion(pricing);
  const anchor = Number(anchorWeighted);
  if (weighted == null || !Number.isFinite(anchor) || anchor <= 0) return null;
  const rounded = Math.round((weighted / anchor) * 10) / 10;
  return Math.max(0.1, rounded);
};
